import React, { useState } from 'react';
import { useAuth } from '../../AuthContext';
import { useLanguage } from '../../App';
import { motion, AnimatePresence } from 'framer-motion';
import { Mail, X, Check, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function EmailVerifyBanner() {
  const { user, token, fetchUser } = useAuth();
  const { language } = useLanguage();
  const isEN = language === 'en';
  const [dismissed, setDismissed] = useState(false);
  const [showInput, setShowInput] = useState(false);
  const [code, setCode] = useState('');
  const [status, setStatus] = useState(null); // 'sending' | 'sent' | 'verifying' | 'verified' | 'error'
  const [errorMsg, setErrorMsg] = useState('');

  if (!user || user.email_verified || dismissed) return null;

  const resend = async () => {
    setStatus('sending');
    try {
      await fetch(`${BACKEND_URL}/api/auth/resend-verification`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` },
      });
      setStatus('sent');
      setShowInput(true);
      setTimeout(() => setStatus(null), 3000);
    } catch {
      setStatus('error');
      setErrorMsg(isEN ? 'Failed to send' : 'Gagal hantar');
    }
  };

  const verify = async () => {
    if (code.length < 6) return;
    setStatus('verifying');
    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/verify-email`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ code }),
      });
      const data = await res.json();
      if (data.verified) {
        setStatus('verified');
        fetchUser();
        setTimeout(() => setDismissed(true), 2000);
      } else {
        setStatus('error');
        setErrorMsg(data.detail || (isEN ? 'Invalid code' : 'Kod tidak sah'));
        setTimeout(() => setStatus(null), 3000);
      }
    } catch {
      setStatus('error');
      setErrorMsg(isEN ? 'Error' : 'Ralat');
    }
  };

  return (
    <motion.div
      data-testid="email-verify-banner"
      initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
      style={{
        background: '#1e293b', border: '1px solid #334155', borderRadius: 14,
        padding: '12px 14px', marginBottom: 12, position: 'relative',
      }}
    >
      <button onClick={() => setDismissed(true)} style={{ position: 'absolute', top: 8, right: 8, background: 'none', border: 'none', color: '#475569', cursor: 'pointer', padding: 4 }}>
        <X size={14} />
      </button>

      {!showInput ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 32, height: 32, borderRadius: 8, background: '#38bdf820', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Mail size={16} color="#38bdf8" />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#f8fafc' }}>
              {isEN ? 'Verify your email' : 'Sahkan e-mel anda'}
            </div>
            <div style={{ fontSize: 11, color: '#94a3b8' }}>
              {isEN ? 'Check your inbox for verification code' : 'Semak peti masuk untuk kod pengesahan'}
            </div>
          </div>
          <button
            data-testid="resend-verify-btn"
            onClick={resend}
            disabled={status === 'sending'}
            style={{
              background: '#38bdf8', color: '#020617', border: 'none', borderRadius: 100,
              padding: '6px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer',
              fontFamily: "'Nunito', sans-serif",
            }}
          >
            {status === 'sending' ? <Loader2 size={12} className="animate-spin" /> : status === 'sent' ? (isEN ? 'Sent!' : 'Dihantar!') : (isEN ? 'Verify' : 'Sahkan')}
          </button>
        </div>
      ) : (
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#f8fafc', marginBottom: 8 }}>
            {isEN ? 'Enter 6-digit code' : 'Masukkan kod 6 digit'}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              data-testid="verify-code-input"
              type="text" inputMode="numeric" maxLength={6} value={code}
              onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
              placeholder="000000"
              style={{
                flex: 1, background: '#0f172a', border: '1px solid #334155', borderRadius: 10,
                padding: '8px 12px', fontSize: 18, fontWeight: 700, color: '#f8fafc',
                textAlign: 'center', letterSpacing: 6, fontFamily: 'monospace', outline: 'none',
              }}
            />
            <button
              data-testid="submit-verify-btn"
              onClick={verify}
              disabled={code.length < 6 || status === 'verifying'}
              style={{
                background: code.length === 6 ? '#34d399' : '#1e293b', color: code.length === 6 ? '#020617' : '#475569',
                border: 'none', borderRadius: 10, padding: '8px 16px', cursor: code.length === 6 ? 'pointer' : 'not-allowed',
                fontWeight: 700, fontSize: 13, fontFamily: "'Nunito', sans-serif",
              }}
            >
              {status === 'verifying' ? '...' : status === 'verified' ? <Check size={16} /> : (isEN ? 'OK' : 'OK')}
            </button>
          </div>
          <AnimatePresence>
            {status === 'verified' && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} style={{ color: '#34d399', fontSize: 12, marginTop: 6, fontWeight: 600 }}>
                {isEN ? 'Email verified!' : 'E-mel disahkan!'}
              </motion.div>
            )}
            {status === 'error' && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} style={{ color: '#f43f5e', fontSize: 12, marginTop: 6 }}>
                {errorMsg}
              </motion.div>
            )}
          </AnimatePresence>
          <button onClick={resend} disabled={status === 'sending'} style={{ background: 'none', border: 'none', color: '#38bdf8', fontSize: 11, marginTop: 6, cursor: 'pointer', padding: 0, fontFamily: "'Nunito', sans-serif" }}>
            {isEN ? 'Resend code' : 'Hantar semula kod'}
          </button>
        </div>
      )}
    </motion.div>
  );
}
